/**
 * Utility functions for working with FHIR document references and IDs
 */

/**
 * Extracts the doc-id-extension from a FHIR resource.
 * This extension contains the document ID that can be used to retrieve the source document.
 *
 * @param resource - Any FHIR resource that may contain a doc-id-extension
 * @returns The document ID string, or undefined if not found
 */
export function getDocIdExtension(resource: unknown): string | undefined {
  if (!resource || typeof resource !== "object") return undefined;

  const resourceObj = resource as {
    extension?: Array<{
      url: string;
      valueString?: string;
      valueReference?: { reference: string };
    }>;
  };

  const docIdExt = resourceObj.extension?.find(
    ext => ext.url === "https://public.metriport.com/fhir/StructureDefinition/doc-id-extension.json"
  );

  return docIdExt?.valueString || docIdExt?.valueReference?.reference;
}

/**
 * Truncates a document ID to a readable length for display purposes.
 * Long document IDs are truncated in the middle with ellipsis.
 *
 * @param docId - The full document ID string
 * @param maxLength - Maximum length before truncation (default: 40)
 * @returns Truncated document ID with ellipsis in the middle if needed
 *
 * @example
 * truncateDocId("cdb678ab-07e3-42c5-93f5-5541cf1f15a8/019885be-755b-746e-937c-51df3b33078c/file.xml", 30)
 * // Returns: "cdb678ab-07e3-4...3b33078c/file.xml"
 */
export function truncateDocId(docId: string, maxLength = 40): string {
  if (docId.length <= maxLength) return docId;

  // Calculate how many characters to show on each side
  const charsPerSide = Math.floor((maxLength - 3) / 2); // 3 for "..."
  const startChars = Math.ceil(charsPerSide);
  const endChars = Math.floor(charsPerSide);

  return `${docId.substring(0, startChars)}...${docId.substring(docId.length - endChars)}`;
}
